import type { SectionRendererProps } from '../section-renderer.types';

interface StatItem {
  label: string;
  value: string;
}

interface StatsBarContent {
  eyebrow?: string;
  stats: StatItem[];
}

/**
 * Stats band (FR-109, FR-120). Home: dark navy background with dark-green
 * accents, eyebrow centered above a centered stat row, per the client's
 * 2026-09-19 design review. Benefits: a different treatment entirely,
 * confirmed from the client's own reference screenshot for this page - a
 * dkgreen band, the eyebrow as its own left-aligned column (not centered,
 * not stacked above), white stat numbers (not dkgreen), all four columns
 * (eyebrow + 3 stats) divided by the same vertical rule.
 */
export function StatsBarRenderer({ section, pageSlug }: SectionRendererProps) {
  const c = section.content as unknown as StatsBarContent;

  if (pageSlug === 'benefits') {
    return (
      <section className="w-full bg-dkgreen">
        <div className="mx-auto flex w-full max-w-328 flex-col items-stretch gap-8 px-4 py-12 sm:flex-row sm:divide-x sm:divide-white/20 sm:py-16">
          {c.eyebrow && (
            <p className="text-p4 max-w-56 font-bold uppercase text-navy sm:flex sm:items-center sm:pr-8">
              {c.eyebrow}
            </p>
          )}
          {c.stats?.map((stat, i) => (
            <div key={i} className="flex flex-1 flex-col items-center justify-center gap-2 text-center sm:px-8">
              <p className="text-h1 text-white">{stat.value}</p>
              <p className="text-p4 text-white">{stat.label}</p>
            </div>
          ))}
        </div>
      </section>
    );
  }

  // Investors (confirmed via Figma's own get_design_context data): same
  // centered layout as Home, but teal background with the lighter brand
  // green (--color-green, #4DBA72) eyebrow, not navy/dkgreen.
  const bgClass = pageSlug === 'investors' ? 'bg-teal' : 'bg-navy';
  const eyebrowClass = pageSlug === 'investors' ? 'text-green' : 'text-dkgreen';

  return (
    <section className={`w-full ${bgClass}`}>
      <div className="mx-auto flex w-full max-w-328 flex-col gap-8 px-4 py-16">
        {c.eyebrow && (
          <p className={`text-p6 text-center font-semibold uppercase ${eyebrowClass}`}>{c.eyebrow}</p>
        )}
        {/* sm:divide-x adds the thin separator line between stats the client's
          design review showed - the only change made here, per instruction
          not to touch anything else about this section. */}
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-3 sm:divide-x sm:divide-white/20">
          {c.stats?.map((stat, i) => (
            <div key={i} className="flex flex-col items-center gap-2 text-center sm:px-8">
              <p className="text-h1 text-white">{stat.value}</p>
              <p className="text-p4 text-white">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
